import { useMemo, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";

import DefaultLayout from "./../layouts/DefaultLayout.web";
import CustomText from "./../components/common/general/CustomText/CustomText";
import CustomButton from "./../components/common/general/CustomButton/CustomButton";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useGetUserAnnouncementsQuery, useMarkAnnouncementAsSeenMutation } from "../graphql/generated/graphql";
import { formatDateWithTime } from "./../utils/date";
import type { RootStackParamList } from "../navigation/navigation.types";
import usePagination from "./../hooks/usePagination";
import AdminTablePagination from "./../components/adminDashboard/AdminTable/AdminTablePagination";

const AnnouncementView = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { isMedium } = useResponsiveScreen();
  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 5 });
  const { data, refetch } = useGetUserAnnouncementsQuery({
    variables: {
      page,
      pageSize,
    },
    fetchPolicy: "network-only",
  });

  const announcements = data?.getUserAnnouncements?.data ?? [];
  const totalCount = data?.getUserAnnouncements?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const [markAnnouncementAsRead] = useMarkAnnouncementAsSeenMutation();

  const displayedAnnouncements = useMemo(
    () => [...announcements].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()),
    [announcements]
  );

  const handleReadMore = useCallback(
    (announcement: (typeof announcements)[number]) => {
      if (announcement.isNew) {
        markAnnouncementAsRead({
          variables: {
            announcementId: announcement.id,
          },
        });
      }
      refetch();
      navigation.navigate("Announcement Detail", {
        announcementId: announcement.id,
      });
    },
    [navigation, markAnnouncementAsRead]
  );

  return (
    <DefaultLayout pageHasChat={false}>
      <View style={styles.headerContainer}>
        <CustomText
          role="header"
          text="Announcements"
          size={isMedium ? "m" : "xs"}
          textType="display"
          weight="ultraBold"
        />
      </View>
      <View style={styles.announcementList}>
        {displayedAnnouncements.length === 0 && (
          <CustomText
            text="It looks like there are no announcements yet."
            style={styles.emptyText}
          />
        )}
        {displayedAnnouncements.map((announcement) => (
          <View key={announcement.id} style={[styles.announcementItem, announcement.isNew && styles.isNew]}>
            <View style={styles.announcementHeader}>
              <View style={styles.announcementTitle}>
                {announcement.isNew && (
                  <View style={styles.newBadge}>
                    <CustomText style={styles.newBadgeText} text="New" />
                  </View>
                )}
                <CustomText
                  style={styles.titleText}
                  text={announcement.title ?? ""}
                  useTranslationText={false}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                />
              </View>
              <View>
                <CustomText
                  text={formatDateWithTime(announcement.created)}
                  size="xs"
                  style={styles.createdDate}
                  useTranslationText={false}
                />
              </View>
            </View>
            <View>
              <CustomText
                text={stripHtmlTags(announcement.message)}
                size="s"
                useTranslationText={false}
                numberOfLines={1}
                ellipsizeMode="tail"
              />
            </View>
            <View>
              <CustomButton
                text={"Read More"}
                useTranslationText={true}
                size="s"
                styleType={"primaryTransparent"}
                onPress={() => {
                  handleReadMore(announcement);
                }}
                style={styles.readmoreBtn}
              />
            </View>
          </View>
        ))}
      </View>
      <View>
        {totalCount > 0 && (
          <AdminTablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
          />
        )}
      </View>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    flexDirection: "column",
    marginBottom: 28,
  },
  announcementList: {
    flexDirection: "column",
    gap: 16,
  },
  announcementItem: {
    padding: 40,
    flexDirection: "column",
    gap: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  isNew: {
    backgroundColor: "#F9FAFB",
    borderColor: "transparent",
  },
  announcementHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  announcementTitle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  newBadge: {
    borderRadius: 100,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: "#007CC1",
  },
  newBadgeText: {
    color: "#FFF",
    fontSize: 12,
    lineHeight: 18,
    fontWeight: 600,
  },
  titleText: {
    fontSize: 18,
    lineHeight: 28,
    fontWeight: 700,
  },
  createdDate: {
    color: "#667085",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 400,
  },
  readmoreBtn: {
    backgroundColor: "#fff",
    width: "fit-content",
    paddingHorizontal: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    borderColor: "#05060F1A",
    borderWidth: 1,
  },
  emptyText: {
    color: "#667085",
  },
});

export default AnnouncementView;
